/**
 * DESIGN B — « LA CARTE »
 * ═════════════════════════════════════════════════════════════════════════
 * Le rendu d'un objet posé sur la page : anneau de progression à gauche,
 * MONTANT EN EUROS au centre de gravité, pilule blanche à droite.
 *
 * ⛔ CE FICHIER NE CALCULE RIEN. Toutes les formules viennent du noyau de
 *   `onix-barre-c.js`, exposé en `window.OnixBarreC` :
 *       lirePalier · gainCentimes · palierAtteint · marcheSuivante
 *       prixMarginalCentimes · decider
 *   La section charge les deux scripts, dans cet ordre, tous deux en `defer`.
 *   Recopier ces six fonctions ici, c'est accepter qu'elles divergent d'un
 *   centime de la caisse Shopify — le prototype C le dit déjà dans son
 *   en-tête, et cart-drawer-promo-suggest.js ligne 183 aussi. Un centime
 *   d'écart annoncé, c'est une promesse non tenue au paiement.
 *   ➜ Si le noyau est absent, la carte reste `hidden` et le DIT dans la
 *     console. Elle ne devine pas un montant, et elle ne se rabat pas sur
 *     une formule locale « en attendant ».
 *   ⚠️ `uniteMinPanier()` n'est PAS une exception : aucune remise, aucun
 *     palier, aucun taux de change. C'est une LECTURE du panier — le prix à
 *     l'unité le plus bas — que le noyau réclame en 5ᵉ argument et que ce
 *     fichier ne lui passait pas. Voir le point 1 ci-dessous.
 *
 * ⛔ ZÉRO POURCENTAGE. Sa décision du 05/09 : « le client il a pas une
 *   calculette en main pour savoir sa 15 % c'est équivalent à combien ».
 *   Aucun `%` n'est produit par ce fichier — cherchez, il n'y en a pas.
 *   L'état « panier vide », seul état sans panier, calcule sur le prix
 *   de référence du catalogue (`data-prix-reference`, 24,99 € par défaut).
 *
 * ── LES SEPT ÉTATS ───────────────────────────────────────────────────────
 *   | panier     | anneau              | intro                        | montant |
 *   |------------|---------------------|------------------------------|---------|
 *   | vide       | pointillé, cible 2  | Ajoute 2 maillots pour débl. |  7,49 € |
 *   | 1 maillot  | 50 %, « 1 »         | Ajoute 1 maillot pour        | 17,50 € |
 *   | 2 maillots | 67 %, « 2 »         | Ajoute 1 maillot pour        | 17,49 € |
 *   | 4 maillots | 80 %, « 4 »         | Ajoute 1 maillot pour        | 15,00 € |
 *   | 5 maillots | plein, coche        | Tu économises déjà           | 34,98 € |
 *   | 6 maillots | plein, coche        | Tu économises déjà           | 41,98 € |
 *   | 9 maillots | plein, coche        | Tu économises déjà           | 69,72 € |
 *
 * ── LES QUATRE DÉFAUTS CORRIGÉS, ET CE QU'ILS COÛTAIENT ──────────────────
 *
 *   1. LE PANIER MIXTE — LE PRIX ANNONCÉ N'EXISTAIT PAS AU CATALOGUE.
 *      Ce fichier appelait `prixMarginalCentimes(tiers, count, base, TAUX)` :
 *      QUATRE arguments. Le 5ᵉ, `uniteCents`, arrivait `undefined` et le
 *      noyau retombait sur la moyenne `base / count`. Sur 1 veste 59,99 € +
 *      1 maillot 24,99 €, la moyenne vaut 42,49 € et la carte annonçait le
 *      maillot suivant à 29,74 € — AU-DESSUS de son prix catalogue (24,99 €),
 *      donc un prix que rien ne vend. La bonne valeur est 15,74 €. Le
 *      correctif du 05/09 était DANS le noyau ; B ne le déclenchait pas.
 *      ➜ `uniteMinPanier()` lit le panier, le 5ᵉ argument est fourni, et le
 *        banc fige 2974 comme « l'ancien faux » pour qu'une régression rougisse.
 *
 *   2. LA PRÉSENCE — TROIS SILENCES SUR HUIT N'ÉTAIENT PAS DES DÉCISIONS.
 *      `decider()` rend `aucun` dans huit cas. QUATRE sont des décisions
 *      prises, et elles ne bougent pas d'un pouce : fiche produit, tiroir de
 *      panier ouvert, clavier mobile, bandeau cookies. Un cinquième est le
 *      geste du client : la croix. Les TROIS derniers n'étaient que des
 *      trous — panier vide sans ancre (donc carte muette sur index, search,
 *      page, blog, article, 404, list-collections), marche suivante à plus
 *      de +2 (5 et 6 maillots : 69 commandes du 07/04 au 04/09, 10,96 % de
 *      la marge), et dernier palier atteint.
 *      ➜ `presence()` les rattrape : la carte RESTE et CHANGE DE PROPOS au
 *        lieu de disparaître. Et la croix vaut désormais aussi à panier vide,
 *        où le noyau ne la lisait pas — elle ne fermait rien.
 *
 *   3. LA RAISON MENTAIT DANS L'INSPECTEUR.
 *      `dataset.raison` était écrit AVANT que `placer()` ait pu échouer :
 *      sur la page panier VIDE, l'inspecteur affichait « page panier — dans
 *      le flux » au-dessus d'une carte masquée. Les ancres `.onix-cart-page__
 *      summary` et `.onix-cart-page__checkout` sont sous `{% if
 *      cart.item_count > 0 %}` dans sections/cart.liquid.
 *      ➜ La raison s'écrit APRÈS le verdict, et un `flux` sans ancre devient
 *        un `flottant` — jamais une disparition silencieuse.
 *
 *   4. « TU ÉCONOMISES DÉJÀ 0,00 € » ÉTAIT ATTEIGNABLE.
 *      `forme()` déclarait `acquis` dès que la marche suivante dépassait
 *      `ecart_max`, SANS vérifier qu'un palier soit atteint. Le champ
 *      « Nombre de maillots » du palier 1 va de 1 à 20 dans le schéma : réglé
 *      à 5, un panier de 1 affichait la coche, l'anneau plein et une économie
 *      NULLE. ➜ `forme()` et `fraction()` posent maintenant la même condition
 *        dans le même ordre — sans palier atteint c'est `cible`, pas `acquis`.
 *        C'est la suite directe du défaut du 05/09 : les deux étaient vrais
 *        séparément et faux ensemble.
 *
 *   5. « AJOUTER » MENAIT AU PANIER — c'est-à-dire nulle part.
 *      Le bouton demandait un maillot de plus et ouvrait le tiroir : le seul
 *      endroit du site où il n'y a aucun maillot à prendre. Le client devait
 *      refermer le tiroir pour faire ce qu'on venait de lui demander.
 *      ➜ `destinationAction()` mène « Ajouter » à la collection
 *        `les-meilleurs-maillots-1` — https://onixm.fr/collections/les-meilleurs-maillots-1,
 *        vérifiée 200 sans redirection, seule collection du catalogue dont le
 *        titre parle de « meilleur » et celle que remplit le cron
 *        `collection-meilleures-ventes.js`.
 *      ⛔ ET « VOIR LE PANIER » VA TOUJOURS AU PANIER. Le MÊME écouteur sert
 *        les deux libellés : rediriger sans regarder `forme()` aurait envoyé
 *        « Voir le panier » sur une grille de maillots — le seul chemin vers
 *        le paiement coupé, au moment où le panier est le plus rempli. Le banc
 *        fige les deux sens, plus le double préfixe de langue `/en/en/…`.
 *
 *   node theme-brouillon/assets/onix-design-b-carte.js --test   # 114 cas figés
 */
(function (racine) {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════
     PARTIE PURE — la COMPOSITION du texte et la géométrie de l'anneau.
     Aucun DOM, donc testable en Node. Aucune formule d'argent : elles sont
     toutes dans le noyau.
     ═══════════════════════════════════════════════════════════════════════ */

  /* Une phrase traduite « Ajoute {{n}} {{item}} pour {{price}} 🔥 » devient
     les DEUX lignes de la carte : ce qui précède le montant (l'intro), et ce
     qui le suit (le suffixe, une emoji le plus souvent).

     ⚠️ ON NE COUPE PAS LA TRADUCTION, ON LA RANGE. Le premier réflexe était
       de retirer l'emoji au passage — « une carte Apple n'a pas d'emoji ».
       C'est réécrire le texte du site depuis un fichier de design, et ça
       vaudrait pour les six langues d'un coup. Le suffixe est donc RENDU,
       à la taille de l'intro : il accompagne le montant au lieu de peser
       autant que lui.
     ⚠️ ET SI LE GABARIT N'A PAS DE {{price}} — traduction future maladroite,
       clé changée — on ne rend pas une carte vide : toute la phrase part dans
       l'intro et le montant est mis à part, derrière elle. Rien ne disparaît.

     Le remplacement des autres marqueurs est fait AVANT par l'appelant, pour
     que cette fonction n'ait qu'un seul travail. */
  function composer(gabarit, montant, marqueur) {
    var g = String(gabarit == null ? '' : gabarit);
    var m = String(marqueur || '{{price}}');
    var i = g.indexOf(m);
    if (i === -1) {
      return { intro: g.trim(), montant: montant, suffixe: '' };
    }
    return {
      intro: g.slice(0, i).trim(),
      montant: montant,
      suffixe: g.slice(i + m.length).trim()
    };
  }

  /* LA FRACTION DE L'ANNEAU, entre 0 et 1.
     · palier acquis et plus rien de demandable → 1 (anneau plein + coche) ;
     · sinon `count / marche_suivante.qty` : 1/2 = 0,50 · 2/3 = 0,67 ·
       4/5 = 0,80. Le rapport direct, parce que sur un ANNEAU un arc de 10 %
       ne se voit pas — c'est ce que donnerait le remplissage en segments
       égaux du prototype C à 1 maillot. La forme choisit la formule.
     ⛔ L'ANNEAU DIT LA MÊME CHOSE QUE LA COCHE — corrigé le 05/09, vu sur la
       capture et pas dans le code. `forme()` déclare `acquis` dès que la
       marche suivante est au-delà de `ecartMax` : à 5 maillots elle est à +4,
       la carte cesse de demander et pose une coche. Mais `fraction()`
       ignorait `ecartMax` et rendait 5/9 = 0,56. La carte affichait donc
       « c'est fait » sur un anneau à moitié vide — deux affirmations
       contraires dans le même objet de 46 px. Les deux cas étaient dans le
       banc, chacun de son côté ; leur CONTRADICTION n'y était pas.
     ⚠️ Le rapport peut toujours reculer, et c'est désormais cohérent : 5
       maillots donne l'anneau plein (elle ne demande rien), 7 maillots
       redescend à 0,78 (la marche à 9 revient dans les clous). L'anneau
       recule quand la carte se remet à pousser — il ne recule plus pendant
       qu'elle dit que c'est fini. */
  function fraction(noyau, tiers, count, ecartMax) {
    if (!count || count <= 0) return 0;
    var suiv = noyau.marcheSuivante(tiers, count);
    var atteint = noyau.palierAtteint(tiers, count);
    if (!suiv) return atteint ? 1 : 0;
    if (ecartMax != null && (suiv.qty - count) > ecartMax) return atteint ? 1 : 0;
    return Math.max(0, Math.min(1, count / suiv.qty));
  }

  /* L'état d'affichage, en un mot. `acquis` = anneau plein et coche.
     ⛔ `acquis` EXIGE UN PALIER ATTEINT — et ce n'était pas le cas. La règle
       « la marche suivante est trop loin » suffisait à poser la coche : avec
       le palier 1 réglé à 5 maillots (le champ va de 1 à 20 dans le schéma),
       un panier de 1 article affichait l'anneau plein, la coche, et
       « Tu économises déjà 0,00 € ». Rien n'était acquis.
     ⚠️ LES TROIS TESTS SONT LES MÊMES QUE CEUX DE `fraction()`, DANS LE MÊME
       ORDRE. C'est la leçon du 05/09 : les deux fonctions étaient vraies
       séparément et se contredisaient à l'écran. Elles ne peuvent plus
       diverger sans que le banc le voie, il teste leur ACCORD. */
  function forme(noyau, tiers, count, ecartMax) {
    if (!count || count <= 0) return 'cible';
    var suiv = noyau.marcheSuivante(tiers, count);
    var atteint = noyau.palierAtteint(tiers, count);
    if (!suiv) return atteint ? 'acquis' : 'cible';
    if (ecartMax != null && (suiv.qty - count) > ecartMax) return atteint ? 'acquis' : 'cible';
    return 'marche';
  }

  /* ═══ OÙ MÈNE LE SEUL BOUTON DE LA CARTE ═══════════════════════════════
     ⛔ LE CHOIX, ET SA RAISON, EN UNE LIGNE : « Ajouter » mène à la collection
       `les-meilleurs-maillots-1` — https://onixm.fr/collections/les-meilleurs-maillots-1,
       vérifiée 200 sans redirection — parce que c'est la SEULE collection du
       catalogue dont le titre parle de « meilleur » (24 collections lues à
       l'API) et celle que remplit `scripts/collection-meilleures-ventes.js` :
       un client à qui on demande un maillot de plus doit tomber sur des
       maillots, pas sur son propre panier.
     ⛔ ET « VOIR LE PANIER » CONTINUE D'ALLER AU PANIER. Le MÊME écouteur sert
       les deux libellés : `forme() === 'marche'` écrit « Ajouter »,
       `'acquis'` écrit « Voir le panier » (cart.second_add / cart.view_cart).
       Rediriger sans regarder l'état aurait envoyé « Voir le panier » sur une
       collection — un libellé qui ment, et le seul chemin vers le paiement
       coupé au moment où le client a le plus rempli son panier. La destination
       se décide donc sur la FORME, et le banc fige les deux sens.
     ⚠️ LE PRÉFIXE DE LANGUE NE SE MET PAS DEUX FOIS. `LROOT` vaut `/en/` sur la
       boutique anglaise. Si le Liquid a déjà rendu une URL localisée, la
       préfixer encore donnerait `/en/en/collections/…`, une 404 — et ne jamais
       la préfixer enverrait l'anglophone sur la page française. On ne préfixe
       donc que ce qui ne l'est pas.
     ⚠️ FONCTION PURE, appelée par l'écouteur ET par le banc, le même chemin.
       C'est la leçon du 05/09 quelques lignes plus bas : un calcul qui vit
       dans le DOM est hors de portée des cas figés, et trois mutations y sont
       restées vertes 85/85. */
  var CHEMIN_COLLECTION = 'collections/les-meilleurs-maillots-1';

  function destinationAction(f, urlCollection, lroot) {
    var root = String(lroot == null || lroot === '' ? '/' : lroot);
    if (root.slice(-1) !== '/') root += '/';
    /* Palier acquis : plus rien à ajouter, le bouton dit « Voir le panier ». */
    if (f === 'acquis') return { url: root + 'cart', panier: true };
    var u = String(urlCollection == null ? '' : urlCollection).trim();
    if (!u) u = root + CHEMIN_COLLECTION;                    /* Liquid muet → repli */
    else if (u.charAt(0) === '/' && root !== '/' && u.indexOf(root) !== 0) u = root + u.slice(1);
    return { url: u, panier: false };
  }

  /* ⛔ LE MOINS CHER À L'UNITÉ, HORS OFFRE DÉCOUVERTE — le 5ᵉ argument que ce
     fichier ne passait pas au noyau.
     Ce n'est PAS une formule d'argent : aucune remise, aucun palier, aucun
     taux. C'est une lecture du panier, et c'est le seul prix à l'unité dont
     on sache qu'il existe au catalogue. Sans lui le noyau retombe sur la
     moyenne `base / count`, juste tant que le panier est homogène et FAUSSE
     dès qu'il ne l'est plus.
     ⚠️ `original_price` D'ABORD, ET CE N'EST PAS INDIFFÉRENT. `base` vient de
       `cart.original_total_price` (avant remise) et le noyau retire lui-même
       la remise du palier. Prendre `final_price` (après remise de ligne)
       mélangerait un total avant et une unité après : le prix marginal
       sortirait trop BAS — l'erreur symétrique de celle qu'on corrige.
     ⚠️ Les lignes de l'offre découverte sont exclues, exactement comme dans
       le décompte : leur prix à l'unité n'est pas un prix de catalogue.
     ⚠️ Rend `null` — jamais 0 — quand il n'y a rien à lire : le noyau teste
       `uniteCents != null && uniteCents > 0`, un 0 le ferait retomber sur la
       moyenne sans le dire. */
  function uniteMinPanier(items) {
    var min = null;
    (items || []).forEach(function (i) {
      if (!i) return;
      if (i.properties && i.properties._offer_bundle === 'true') return;
      var u = i.original_price;
      if (!(u > 0)) u = i.final_price;
      if (!(u > 0)) u = i.price;
      if (u > 0 && (min === null || u < min)) min = u;
    });
    return min;
  }

  /* ═══ LE PRIX DE LA MARCHE SUIVANTE — SORTI DU DOM LE 05/09 À 14h20 ════
     ⛔ POURQUOI CETTE FONCTION EXISTE, ET C'EST LA FAUTE LA PLUS CHÈRE DE
       LA JOURNÉE. Le calcul vivait DANS `rendre()`, donc dans le DOM, donc
       hors de portée du banc. Les cas figés appelaient alors
       `noyau.prixMarginalCentimes(...)` EN DIRECT — c'est-à-dire qu'ils
       testaient onix-barre-c.js, pas ce fichier. Résultat mesuré, mutations
       rejouées à la main sur copie isolée :

         retirer le 5ᵉ argument            → banc VERT, 85/85
         forcer uniteMin à null            → banc VERT, 85/85
         `u > 0` devenu `u >= 0`           → banc VERT, 85/85

       Trois façons de faire réapparaître « 29,74 € » pour un maillot vendu
       24,99 €, et pas un seul rouge. Un banc vert sur du code cassé n'est
       pas une protection : c'est une décoration qui rassure.
     ➜ La décision de prix est donc une fonction PURE, appelée par `rendre()`
       et par le banc — le même chemin, pas une copie. Une mutation ici
       rougit maintenant.
     ⚠️ `noyau` est passé en argument plutôt que lu dans `racine` : le banc
       tourne dans Node, où `window.OnixBarreC` n'existe pas. */
  function prixMarcheSuivante(noyau, tiers, count, base, taux, items) {
    /* ⛔ LE 5ᵉ ARGUMENT. Sans lui le noyau retombe sur `base / count` :
       1 veste 59,99 € + 1 maillot 24,99 € donnaient 29,74 € annoncés pour le
       maillot suivant, au-dessus de son propre prix catalogue. */
    return noyau.prixMarginalCentimes(tiers, count, base, taux, uniteMinPanier(items));
  }

  /* ⛔ CE QUI OUVRE UN CLAVIER, ET CE QUI N'EN OUVRE PAS — 05/09.
     Le test s'appelait `estChampTexte()`, vivait dans `demarrer()` — donc hors
     de portée de tout cas figé — et acceptait `select`. Or un `<select>` EST un
     champ mais n'ouvre AUCUN clavier : il ouvre une roue de choix, qui ne réduit
     pas le viewport. Le tri de collection (`sections/collection.liquid`,
     `#onix-collection__sort-select`) faisait donc taire la carte à chaque tri.
     Mesuré au navigateur le 05/09 : sur `focusin`, `hidden` passait à true et
     `data-raison` à « clavier mobile ouvert ».
     ⚠️ COPIE VOLONTAIRE DE `noyau.ouvreUnClavier` — la carte ne prend pas une
       dépendance de plus sur onix-barre-c.js. Elle refuse déjà de démarrer sans
       lui (§B2 du diagnostic du 05/09) ; élargir cette surface pour six lignes
       de booléen ferait tomber la carte entière le jour où le CDN sert un noyau
       plus vieux. ➜ Le banc compare les DEUX copies : la divergence rougit. */
  var TYPES_SANS_CLAVIER = {
    checkbox: 1, radio: 1, button: 1, submit: 1, reset: 1,
    range: 1, file: 1, image: 1, color: 1, hidden: 1
  };

  function ouvreUnClavier(el) {
    if (!el) return false;
    var t = String(el.tagName || '').toLowerCase();
    if (t === 'textarea') return true;
    if (t === 'input') return !TYPES_SANS_CLAVIER[String(el.type || 'text').toLowerCase()];
    return el.isContentEditable === true;
  }

  /* ═══ LA PRÉSENCE — OÙ LA CARTE VA, ET POURQUOI ═══════════════════════
     Le noyau `decider()` est partagé avec le prototype C, et il rend `aucun`
     dans HUIT cas. Quatre sont des décisions prises, une est un geste du
     client, trois n'étaient que des trous. Cette fonction fait le tri —
     elle ne recopie aucune formule et n'appelle qu'une fois le noyau.

     ⛔ LES QUATRE SILENCES DÉLIBÉRÉS NE BOUGENT PAS, ET ILS SONT TESTÉS ICI
       PLUTÔT QUE DÉDUITS DE `d.raison` : une raison est une phrase française
       destinée à l'inspecteur, pas un contrat. La lire pour décider, c'est
       accepter qu'une reformulation change le comportement.
         · fiche produit    — le bouton d'ajout collant dit déjà quoi faire
         · tiroir ouvert    — l'échelle et l'offre y sont, en entier
         · clavier mobile   — le viewport réduit collerait la carte au milieu
         · bandeau cookies  — il occupe déjà le bas de l'écran

     ⛔ ET LA CROIX RESTE UNE DÉCISION DU CLIENT. Elle ne vaut que pour le
       mode flottant : c'est le seul où elle existe. En flux la carte défile
       avec la page, il n'y a rien à fermer — donc rien n'a pu l'être, et une
       carte fermée sur une page de collection ne doit pas faire disparaître
       le rappel au-dessus du bouton de paiement. */
  function presence(noyau, etat) {
    var d = noyau.decider(etat);

    if (etat.page === 'product' || etat.tiroirOuvert || etat.clavier || etat.cookies) return d;

    /* Aucun palier : rien à annoncer, et `tiers[0]` n'existe pas. */
    if (!(etat.tiers && etat.tiers.length))
      return { mode: 'aucun', raison: 'aucun palier configuré — la carte n’a rien à annoncer' };

    var mode, raison;
    if (d.mode === 'flux' && etat.ancre) {
      mode = 'flux'; raison = d.raison;
    } else if (d.mode === 'flux') {
      /* Le noyau annonce `flux` sur la page panier MÊME VIDE, où les ancres
         sont sous `{% if cart.item_count > 0 %}`. C'est là que la carte
         disparaissait en affichant « dans le flux » dans l'inspecteur. */
      mode = 'flottant'; raison = 'aucune ancre dans cette page — flottante plutôt qu’absente';
    } else if (d.mode === 'flottant') {
      mode = 'flottant'; raison = d.raison;
    } else if (!etat.count || etat.count <= 0) {
      mode = 'flottant'; raison = 'panier vide et aucune ancre ici — elle flotte et promet, en euros';
    } else {
      /* Le mur (+3, +4) et le dernier palier : plus rien de DEMANDABLE, mais
         quelque chose à CONSTATER. La carte ne pousse pas dans le vide, elle
         dit ce qui est déjà gagné. C'est le trou de 5 et 6 maillots. */
      mode = 'flottant'; raison = 'rien de demandable (' + d.raison + ') — elle reste et constate';
    }

    if (mode === 'flottant' && etat.ferme)
      return { mode: 'aucun', raison: 'fermée par le client, et le panier n’a pas bougé depuis' };

    return { mode: mode, raison: raison };
  }

  var noyauLocal = {
    composer: composer,
    fraction: fraction,
    forme: forme,
    destinationAction: destinationAction,
    uniteMinPanier: uniteMinPanier,
    prixMarcheSuivante: prixMarcheSuivante,
    ouvreUnClavier: ouvreUnClavier,
    presence: presence
  };

  /* ═══════════════════════════════════════════════════════════════════════
     BANC FIGÉ — `node theme-brouillon/assets/onix-design-b-carte.js --test`
     La moitié des cas sont des « doit AFFICHER » : un banc validé seulement
     sur ce qu'il refuse ne prouve rien.
     ⚠️ Il rejoue AUSSI les six montants de l'aperçu, par le noyau de
       onix-barre-c.js chargé depuis ce même dossier. Si le noyau bouge, ce
       banc tombe — c'est précisément ce qu'on veut : deux fichiers qui
       affichent le même euro doivent tomber ensemble.
     ═══════════════════════════════════════════════════════════════════════ */
  var PALIERS_REELS = [
    { qty: 2, pct: 15 },
    { qty: 3, pct: 20 },
    { qty: 4, pct: 25 },
    { qty: 5, pct: 28 },
    { qty: 9, pct: 31 }
  ];

  function banc(noyau) {
    var T = PALIERS_REELS, ok = 0, ko = 0;
    function verifier(nom, obtenu, attendu) {
      var bon = String(obtenu) === String(attendu);
      if (bon) { ok++; console.log('  ✅ ' + nom + '  → ' + obtenu); }
      else { ko++; console.log('  ⛔ ' + nom + '  → ' + obtenu + '   (attendu ' + attendu + ')'); }
    }

    console.log('\n── LES SIX MONTANTS DE L’APERÇU, en centimes — aucun pourcentage nulle part');
    /* Panier vide : 2 × 24,99 = 49,98 ; 15 % = 7,49 (floor). C'est le PLANCHER :
       deux maillots plus chers économisent davantage, jamais moins. */
    verifier('vide       → débloque', noyau.gainCentimes(T[0], 2499 * 2, 1), 749);
    /* ⛔ LES SIX CAS QUI MANQUAIENT — 05/09 14h20. Ceux du dessous appelaient
       `noyau.prixMarginalCentimes()` EN DIRECT : ils testaient onix-barre-c.js
       et laissaient CE fichier entièrement à découvert. Trois mutations du
       code réel restaient vertes 85/85. Ceux-ci passent par
       `prixMarcheSuivante()`, le chemin que `rendre()` emprunte lui aussi. */
    var VESTE = { original_price: 5999, quantity: 1 };
    var MAILLOT = { original_price: 2499, quantity: 1 };
    var pms = function (n, b, items) { return noyauLocal.prixMarcheSuivante(noyau, T, n, b, 1, items); };

    verifier('B · mixte veste+maillot → le 3ᵉ au moins cher', pms(2, 8498, [VESTE, MAILLOT]), 1574);
    verifier('B · panier vide de liste → la moyenne, comme avant', pms(2, 4998, []), 1749);
    verifier('B · 2 maillots homogène → identique à la moyenne', pms(2, 4998, [MAILLOT, MAILLOT]), 1749);
    /* La ligne de l'offre découverte ne donne pas un prix de catalogue : si elle
       comptait, un bundle à 29,99 € pour 2 sortirait un minimum de 29,99 €. */
    verifier('B · la ligne offre découverte est ignorée',
      pms(2, 8498, [VESTE, MAILLOT, { original_price: 1499, properties: { _offer_bundle: 'true' } }]), 1574);
    /* Un article à 0 € (cadeau) ne doit pas devenir le « moins cher » : il ferait
       tomber la marche à 0 et la carte annoncerait « le suivant à 0,00 € ». */
    /* ⚠️ LES TROIS CHAMPS À ZÉRO, PAS UN SEUL. Premier jet : `{ original_price: 0 }`
       seul — la cascade retombait sur `final_price` puis `price`, tous deux
       `undefined`, et `undefined >= 0` est FAUX. Le cas passait donc pour de
       mauvaises raisons et laissait la mutation `u > 0` → `u >= 0` en vert. */
    verifier('B · un article à 0 € ne prend pas la place du moins cher',
      pms(2, 8498, [VESTE, MAILLOT, { original_price: 0, final_price: 0, price: 0, quantity: 1 }]), 1574);
    /* ⛔ C'EST LE PRIX CATALOGUE QUI COMPTE, PAS LE PRIX REMISÉ. Un maillot déjà
       remisé à 19,99 € ne doit pas tirer la marche suivante vers le bas : le
       client ne rachètera pas forcément un article en promotion. Sans ce cas,
       inverser `original_price` et `final_price` ne faisait rougir personne. */
    verifier('B · une ligne remisée garde son prix catalogue',
      pms(2, 8498, [VESTE, { original_price: 2499, final_price: 1999, quantity: 1 }]), 1574);
    verifier('B · dernier palier atteint → rien à proposer', pms(9, 22491, [MAILLOT]), null);

    verifier('1 maillot  → prix du 2ᵉ', noyau.prixMarginalCentimes(T, 1, 2499, 1), 1750);
    verifier('2 maillots → prix du 3ᵉ', noyau.prixMarginalCentimes(T, 2, 4998, 1), 1749);
    verifier('4 maillots → prix du 5ᵉ', noyau.prixMarginalCentimes(T, 4, 9996, 1), 1500);
    verifier('5 maillots → acquis', noyau.gainCentimes(T[3], 12495, 1), 3498);
    verifier('9 maillots → acquis', noyau.gainCentimes(T[4], 22491, 1), 6972);

    console.log('\n── LA COMPOSITION EN DEUX LIGNES (la traduction se range, elle ne se coupe pas)');
    var c1 = composer('Ajoute 1 maillot pour {{price}} 🔥', '17,50€');
    verifier('intro', c1.intro, 'Ajoute 1 maillot pour');
    verifier('montant', c1.montant, '17,50€');
    verifier('suffixe (emoji conservée)', c1.suffixe, '🔥');
    var c2 = composer('Tu économises déjà {{amount}} 💰', '34,98€', '{{amount}}');
    verifier('intro économie', c2.intro, 'Tu économises déjà');
    verifier('suffixe économie', c2.suffixe, '💰');
    /* Gabarit sans marqueur : rien ne disparaît, la phrase entière va en intro. */
    var c3 = composer('Phrase sans marqueur', '7,49€');
    verifier('gabarit cassé → intro entière', c3.intro, 'Phrase sans marqueur');
    verifier('gabarit cassé → montant gardé', c3.montant, '7,49€');
    /* Marqueur en tête : l'intro est vide, la carte n'affiche qu'un montant. */
    var c4 = composer('{{price}} de moins', '7,49€');
    verifier('marqueur en tête → intro vide', c4.intro, '');
    verifier('marqueur en tête → suffixe', c4.suffixe, 'de moins');

    console.log('\n── L’ANNEAU — DOIT SE REMPLIR');
    verifier('1 maillot  → 50 %', Math.round(fraction(noyau, T, 1, 2) * 100), 50);
    verifier('2 maillots → 67 %', Math.round(fraction(noyau, T, 2, 2) * 100), 67);
    verifier('4 maillots → 80 %', Math.round(fraction(noyau, T, 4, 2) * 100), 80);
    verifier('9 maillots → plein', fraction(noyau, T, 9, 2), 1);
    verifier('panier vide → vide', fraction(noyau, T, 0, 2), 0);
    /* ⛔ LE CAS QUI MANQUAIT — l'anneau et la coche doivent dire pareil.
       Il ne suffit pas que `forme(5)` rende `acquis` et que `fraction(5)`
       rende quelque chose : les deux sont VRAIS séparément et FAUX ensemble.
       Ce cas-ci teste l'accord, pas chaque moitié. */
    verifier('5 maillots → plein (la coche ne ment pas)', fraction(noyau, T, 5, 2), 1);
    verifier('7 maillots → 78 % (elle repousse, l’anneau recule)',
      Math.round(fraction(noyau, T, 7, 2) * 100), 78);

    console.log('\n── LA FORME — cible / marche / acquis');
    verifier('panier vide  → cible', forme(noyau, T, 0, 2), 'cible');
    verifier('1 maillot    → marche', forme(noyau, T, 1, 2), 'marche');
    verifier('4 maillots   → marche', forme(noyau, T, 4, 2), 'marche');
    /* 5 maillots : la marche suivante est à +4, un mur. La carte ne demande
       plus rien, elle constate — anneau plein et coche. */
    verifier('5 maillots   → acquis (le mur à 9)', forme(noyau, T, 5, 2), 'acquis');
    verifier('9 maillots   → acquis (dernier palier)', forme(noyau, T, 9, 2), 'acquis');

    console.log('\n── LE PANIER MIXTE — un montant annoncé doit EXISTER au catalogue');
    /* La lecture du panier d'abord : aucune remise, aucun palier, aucun taux. */
    verifier('mixte veste+maillot → le moins cher, doit PASSER',
      uniteMinPanier([{ original_price: 5999, quantity: 1 }, { original_price: 2499, quantity: 1 }]), 2499);
    verifier('panier homogène → le même prix, doit PASSER',
      uniteMinPanier([{ original_price: 2499 }, { original_price: 2499 }]), 2499);
    verifier('l’offre découverte est REFUSÉE du calcul',
      uniteMinPanier([{ original_price: 1499, properties: { _offer_bundle: 'true' } }, { original_price: 2499 }]), 2499);
    verifier('repli sur `final_price` puis `price`, doit PASSER',
      uniteMinPanier([{ final_price: 2999 }, { price: 2499 }]), 2499);
    verifier('un prix à 0 est REFUSÉ (sinon le noyau retomberait sur la moyenne)',
      uniteMinPanier([{ original_price: 0 }, { original_price: 2499 }]), 2499);
    verifier('panier vide → null, jamais 0', uniteMinPanier([]), null);
    /* ⛔ LE DÉFAUT DU 05/09, DES DEUX CÔTÉS. 1 veste 59,99 € + 1 maillot
       24,99 € = 84,98 €. Moyenne 42,49 € — un prix que rien ne vend.
       Palier 3 : 84,98 + 24,99 = 109,97 ; −20 % = 21,99 → 87,98.
       Palier 2 aujourd'hui : 84,98 − 15 % (12,74) = 72,24. Écart = 15,74 €. */
    verifier('B passe le 5ᵉ argument → 15,74 €, doit PASSER',
      noyau.prixMarginalCentimes(T, 2, 8498, 1,
        uniteMinPanier([{ original_price: 5999 }, { original_price: 2499 }])), 1574);
    verifier('sans le 5ᵉ argument → l’ANCIEN FAUX, figé', noyau.prixMarginalCentimes(T, 2, 8498, 1), 2974);
    verifier('1 veste + 3 maillots → 13,95 €, doit PASSER', noyau.prixMarginalCentimes(T, 4, 13496, 1, 2499), 1395);
    verifier('… sans le 5ᵉ argument → l’ANCIEN FAUX, figé', noyau.prixMarginalCentimes(T, 4, 13496, 1), 2025);
    /* LA BORNE, celle qui aurait suffi à voir le défaut sans lire le code :
       le prix du maillot suivant ne peut pas dépasser son prix catalogue, la
       remise du palier ne fait que le baisser. */
    verifier('borne : 15,74 € ≤ 24,99 € → PASSE',
      noyau.prixMarginalCentimes(T, 2, 8498, 1, 2499) <= 2499, true);
    verifier('borne : l’ancien faux la dépassait → REFUSÉ',
      noyau.prixMarginalCentimes(T, 2, 8498, 1) <= 2499, false);
    /* Panier homogène : le 5ᵉ argument ne change RIEN, par construction —
       sinon le correctif aurait déplacé les six montants de l'aperçu. */
    verifier('homogène AVEC le 5ᵉ argument, doit PASSER', noyau.prixMarginalCentimes(T, 2, 4998, 1, 2499), 1749);
    verifier('homogène SANS → identique, doit PASSER', noyau.prixMarginalCentimes(T, 2, 4998, 1), 1749);

    console.log('\n── OÙ MÈNE LE BOUTON — « Ajouter » vers la collection, « Voir le panier » vers le panier');
    var COL = '/collections/les-meilleurs-maillots-1';
    /* Le sens « doit PASSER » : la marche demande un maillot de plus, et on
       l'envoie là où il y a des maillots. */
    verifier('marche → la collection, doit PASSER', destinationAction('marche', COL, '/').url, COL);
    verifier('marche → ce n’est PAS le panier (drapeau tiroir REFUSÉ)',
      destinationAction('marche', COL, '/').panier, false);
    /* Le sens « doit REFUSER » : un palier acquis ne part pas en collection.
       Sans ce cas, une redirection posée sans regarder l'état enverrait
       « Voir le panier » sur une grille de maillots — et personne ne paierait. */
    verifier('acquis → le panier, la collection est REFUSÉE', destinationAction('acquis', COL, '/').url, '/cart');
    verifier('acquis → le tiroir est autorisé', destinationAction('acquis', COL, '/').panier, true);
    /* `cible` : le bouton est masqué (`actionEl.hidden = true`), mais si un jour
       il ne l'était plus, sa destination doit être la collection — pas le panier
       d'un client qui n'a rien dedans. */
    verifier('cible → la collection aussi, doit PASSER', destinationAction('cible', COL, '/').url, COL);
    /* Liquid muet (attribut absent, section non déployée) : on ne rend jamais
       une chaîne vide, qui rechargerait la page en place. */
    verifier('sans attribut Liquid → repli sur le chemin en dur, doit PASSER',
      destinationAction('marche', '', '/').url, COL);
    verifier('attribut absent (undefined) → même repli, doit PASSER',
      destinationAction('marche', undefined, '/').url, COL);
    verifier('borne : la destination n’est jamais vide → PASSE',
      destinationAction('marche', '', '/').url.length > 1, true);
    /* La racine de langue. Deux fautes symétriques, une dans chaque sens. */
    verifier('racine /en/ + URL non préfixée → préfixée une fois, doit PASSER',
      destinationAction('marche', COL, '/en/').url, '/en' + COL);
    verifier('racine /en/ + URL déjà préfixée → PAS de double préfixe (REFUSÉ)',
      destinationAction('marche', '/en' + COL, '/en/').url, '/en' + COL);
    verifier('racine sans slash final → normalisée, doit PASSER',
      destinationAction('marche', COL, '/en').url, '/en' + COL);
    verifier('racine /en/ + acquis → le panier anglais, doit PASSER',
      destinationAction('acquis', COL, '/en/').url, '/en/cart');
    verifier('racine vide → la racine du site, doit PASSER',
      destinationAction('marche', '', '').url, COL);
    /* URL absolue rendue par Liquid : on n'y touche pas, la préfixer donnerait
       `/en/https://…`. */
    verifier('URL absolue → conservée telle quelle, doit PASSER',
      destinationAction('marche', 'https://onixm.fr' + COL, '/en/').url, 'https://onixm.fr' + COL);
    /* Le raccord avec `forme()` : c'est LUI qui décide, et il est déjà figé
       plus haut. Ces deux cas prouvent que les deux fonctions s'accordent —
       5 maillots dit « acquis », donc le panier ; 1 maillot dit « marche »,
       donc la collection. Séparément vraies, elles pourraient diverger. */
    verifier('1 maillot (forme=marche) → la collection, doit PASSER',
      destinationAction(forme(noyau, T, 1, 2), COL, '/').url, COL);
    verifier('5 maillots (forme=acquis) → le panier, doit PASSER',
      destinationAction(forme(noyau, T, 5, 2), COL, '/').url, '/cart');

    console.log('\n── LA PRÉSENCE — DOIT SE TAIRE (les quatre silences délibérés, intacts)');
    verifier('fiche produit → REFUSÉE', presence(noyau, { count: 1, tiers: T, page: 'product' }).mode, 'aucun');
    verifier('tiroir de panier ouvert → REFUSÉE', presence(noyau, { count: 1, tiers: T, tiroirOuvert: true }).mode, 'aucun');
    verifier('clavier mobile ouvert → REFUSÉE', presence(noyau, { count: 1, tiers: T, clavier: true }).mode, 'aucun');
    verifier('bandeau cookies levé → REFUSÉE', presence(noyau, { count: 1, tiers: T, cookies: true }).mode, 'aucun');
    /* Les quatre passent AVANT la page panier et AVANT le rattrapage : le
       nouveau repli flottant ne peut pas les contourner. */
    verifier('page panier + tiroir ouvert → REFUSÉE',
      presence(noyau, { count: 3, tiers: T, page: 'cart', ancre: true, tiroirOuvert: true }).mode, 'aucun');
    verifier('fiche produit + 5 maillots (le rattrapage ne s’y applique pas) → REFUSÉE',
      presence(noyau, { count: 5, tiers: T, page: 'product' }).mode, 'aucun');
    verifier('fiche produit + panier vide → REFUSÉE',
      presence(noyau, { count: 0, tiers: T, page: 'product' }).mode, 'aucun');
    verifier('fermée par le client → REFUSÉE',
      presence(noyau, { count: 1, tiers: T, page: 'index', ferme: true }).mode, 'aucun');
    /* La croix ne fermait RIEN à panier vide : le noyau teste `ferme` après
       avoir déjà répondu sur `count <= 0`. */
    verifier('fermée, panier VIDE (la croix sert enfin) → REFUSÉE',
      presence(noyau, { count: 0, tiers: T, page: 'index', ferme: true }).mode, 'aucun');
    verifier('fermée, page panier vide (flottante donc fermable) → REFUSÉE',
      presence(noyau, { count: 0, tiers: T, page: 'cart', ancre: false, ferme: true }).mode, 'aucun');
    verifier('aucun palier configuré → REFUSÉE', presence(noyau, { count: 3, tiers: [], page: 'index' }).mode, 'aucun');

    console.log('\n── LA PRÉSENCE — DOIT SE MONTRER (le trou de pages, rebouché)');
    verifier('index, panier vide, aucune ancre → doit PASSER',
      presence(noyau, { count: 0, tiers: T, page: 'index' }).mode, 'flottant');
    verifier('… le noyau seul disait « aucun » — L’ANCIEN TROU, figé',
      noyau.decider({ count: 0, tiers: T, page: 'index' }).mode, 'aucun');
    verifier('404, panier vide → doit PASSER', presence(noyau, { count: 0, tiers: T, page: '404' }).mode, 'flottant');
    verifier('blog, 2 maillots → doit PASSER', presence(noyau, { count: 2, tiers: T, page: 'blog' }).mode, 'flottant');
    verifier('search, 1 maillot → doit PASSER', presence(noyau, { count: 1, tiers: T, page: 'search' }).mode, 'flottant');
    verifier('collection avec ancre, panier vide → dans le flux, doit PASSER',
      presence(noyau, { count: 0, tiers: T, page: 'collection', ancre: true }).mode, 'flux');
    verifier('page panier, 3 articles → dans le flux, doit PASSER',
      presence(noyau, { count: 3, tiers: T, page: 'cart', ancre: true }).mode, 'flux');
    /* ⛔ LA RAISON QUI MENTAIT. Le noyau dit « flux » sur la page panier même
       VIDE, où les deux ancres sont sous `{% if cart.item_count > 0 %}` :
       `placer()` échouait, la carte se masquait, et l'inspecteur affichait
       « page panier — dans le flux ». */
    verifier('page panier VIDE → flottante, plus de disparition, doit PASSER',
      presence(noyau, { count: 0, tiers: T, page: 'cart', ancre: false }).mode, 'flottant');
    verifier('… le noyau seul disait « flux » — L’ANCIEN MENSONGE, figé',
      noyau.decider({ count: 0, tiers: T, page: 'cart' }).mode, 'flux');
    verifier('… et la raison ne parle plus de flux',
      presence(noyau, { count: 0, tiers: T, page: 'cart', ancre: false }).raison.indexOf('flux'), -1);
    verifier('fermée ailleurs, mais page panier avec ancre → elle reste, doit PASSER',
      presence(noyau, { count: 3, tiers: T, page: 'cart', ancre: true, ferme: true }).mode, 'flux');

    /* ⛔ LE DÉFAUT « SUR PC ELLE N'APPARAÎT PAS » — 05/09. Le bandeau cookies
       la fait taire, ça c'était voulu et figé plus haut. Ce qui manquait,
       c'est l'autre sens : QU'ELLE REVIENNE une fois la réponse donnée. Le
       cas ci-dessous fige la décision ; le réveil, lui, est un observateur
       du DOM, et il se prouve dans un vrai navigateur —
       `node scripts/banc-carte-b-bureau.mjs`, cas 3.
       ⚠️ LA LARGEUR D'ÉCRAN N'ENTRE PAS ICI, ET C'EST VOLONTAIRE. `presence()`
         ne décide QUE du mode (flux / flottant / aucun) ; où la carte se pose
         sur un grand écran est une affaire de CSS, figée par le banc
         navigateur. Ajouter un faux argument `bureau` à cette fonction
         donnerait un banc vert sur une règle CSS qu'il ne lit pas. */
    console.log('\n── LE CONSENTEMENT — elle se tait, PUIS elle revient');
    verifier('bandeau levé, 3 maillots → REFUSÉE',
      presence(noyau, { count: 3, tiers: T, page: 'index', cookies: true }).mode, 'aucun');
    verifier('… et la raison le dit EN MOTS, pas en code',
      presence(noyau, { count: 3, tiers: T, page: 'index', cookies: true }).raison.indexOf('cookies') !== -1, true);
    verifier('réponse donnée, 3 maillots → elle REVIENT, doit PASSER',
      presence(noyau, { count: 3, tiers: T, page: 'index', cookies: false }).mode, 'flottant');
    verifier('… et la raison ne parle plus du bandeau',
      presence(noyau, { count: 3, tiers: T, page: 'index', cookies: false }).raison.indexOf('cookies'), -1);
    verifier('réponse donnée, page panier avec ancre → dans le flux, doit PASSER',
      presence(noyau, { count: 3, tiers: T, page: 'cart', ancre: true, cookies: false }).mode, 'flux');
    verifier('réponse donnée, panier vide sur index → elle promet, doit PASSER',
      presence(noyau, { count: 0, tiers: T, page: 'index', cookies: false }).mode, 'flottant');

    console.log('\n── LE TROU DE L’ÉCHELLE — 5 et 6 maillots : ce qui est DÉJÀ gagné');
    verifier('5 maillots → la carte reste, doit PASSER',
      presence(noyau, { count: 5, tiers: T, page: 'index' }).mode, 'flottant');
    verifier('… le noyau seul disait « aucun » — L’ANCIEN TROU, figé', noyau.decider({ count: 5, tiers: T }).mode, 'aucun');
    verifier('6 maillots → la carte reste, doit PASSER',
      presence(noyau, { count: 6, tiers: T, page: 'index' }).mode, 'flottant');
    verifier('9 maillots, dernier palier → la carte reste, doit PASSER',
      presence(noyau, { count: 9, tiers: T, page: 'index' }).mode, 'flottant');
    verifier('… le noyau seul disait « aucun » — figé', noyau.decider({ count: 9, tiers: T }).mode, 'aucun');
    /* Elle ne pousse pas dans le vide : elle constate, en euros. */
    verifier('5 maillots → acquis, doit PASSER', forme(noyau, T, 5, 2), 'acquis');
    verifier('6 maillots → acquis, doit PASSER', forme(noyau, T, 6, 2), 'acquis');
    verifier('5 maillots → 34,98 € déjà gagnés', noyau.gainCentimes(T[3], 2499 * 5, 1), 3498);
    verifier('6 maillots → 41,98 € déjà gagnés', noyau.gainCentimes(T[3], 2499 * 6, 1), 4198);
    verifier('borne : le gain acquis ne dépasse pas le panier → PASSE',
      noyau.gainCentimes(T[3], 2499 * 5, 1) <= 2499 * 5, true);
    verifier('borne : et il n’est pas nul → PASSE', noyau.gainCentimes(T[3], 2499 * 5, 1) > 0, true);
    /* 7 et 8 ne sont PAS dans le trou — la marche revient à +2 puis +1, et le
       banc n'avait aucun cas à 6, 7 ni 8. C'est ce qui a laissé passer la
       prémisse « muette de 5 à 8 ». */
    verifier('7 maillots → marche, PAS acquis', forme(noyau, T, 7, 2), 'marche');
    verifier('8 maillots → marche, PAS acquis', forme(noyau, T, 8, 2), 'marche');
    /* 8 × 24,99 = 199,92 ; +1 = 224,91 ; −31 % = 69,72 → 155,19.
       Aujourd'hui : 199,92 − 28 % (55,97) = 143,95. Écart = 11,24 €.
       ⚠️ J'AVAIS ÉCRIT 1223 ICI — deux chiffres transposés, et c'est le banc
         qui l'a attrapé au premier lancement, pas ma relecture. Le 9ᵉ maillot
         coûte MOINS que son prix catalogue parce que le palier passe de 28 à
         31 % : 11,24 € contre 24,99 €. C'est le genre de valeur qu'on ne
         devine pas, donc qu'on ne recopie pas. */
    verifier('8 maillots → prix du 9ᵉ, doit PASSER', noyau.prixMarginalCentimes(T, 8, 2499 * 8, 1, 2499), 1124);

    console.log('\n── « TU ÉCONOMISES DÉJÀ 0,00 € » — impossible, dans les deux sens');
    /* Échelle décalée : le champ « Nombre de maillots » du palier 1 va de 1 à
       20 dans le schéma. Réglé à 5, un panier de 1 affichait la coche et
       l'anneau plein pour une économie NULLE. */
    var TD = [{ qty: 5, pct: 28 }, { qty: 9, pct: 31 }];
    verifier('1 maillot, 1ᵉʳ palier à 5 → cible : rien n’est acquis, doit PASSER', forme(noyau, TD, 1, 2), 'cible');
    verifier('… et l’anneau dit la MÊME chose : vide', fraction(noyau, TD, 1, 2), 0);
    verifier('5 maillots sur la même échelle → acquis, doit PASSER', forme(noyau, TD, 5, 2), 'acquis');
    verifier('… et l’anneau est plein', fraction(noyau, TD, 5, 2), 1);
    verifier('échelle vide, 3 articles → cible, jamais acquis', forme(noyau, [], 3, 2), 'cible');
    verifier('… anneau vide', fraction(noyau, [], 3, 2), 0);
    /* Et l'échelle réelle ne bouge pas d'un cran : le garde ne coûte rien. */
    verifier('échelle réelle, 1 maillot → marche (inchangé), doit PASSER', forme(noyau, T, 1, 2), 'marche');
    verifier('échelle réelle, 5 maillots → acquis (inchangé), doit PASSER', forme(noyau, T, 5, 2), 'acquis');

    console.log('\n── LE CLAVIER — un <select> n’en ouvre pas (le tri de collection)');
    /* ⛔ LE DÉFAUT DU 05/09, mesuré au navigateur : un `focusin` sur le
       `<select>` de tri masquait la carte, raison « clavier mobile ouvert ».
       Aucun clavier ne s'ouvre sur un select. */
    verifier('<select> de tri → la carte RESTE, doit PASSER',
      presence(noyau, { count: 1, tiers: T, page: 'collection', ancre: true,
        clavier: ouvreUnClavier({ tagName: 'SELECT' }) }).mode, 'flottant');
    verifier('<input type=text> (la recherche) → la carte est REFUSÉE',
      presence(noyau, { count: 1, tiers: T, page: 'collection', ancre: true,
        clavier: ouvreUnClavier({ tagName: 'INPUT', type: 'text' }) }).mode, 'aucun');
    verifier('le test lui-même : <select> → doit PASSER', ouvreUnClavier({ tagName: 'SELECT' }), false);
    verifier('le test lui-même : <input type=text> → REFUSÉE',
      ouvreUnClavier({ tagName: 'INPUT', type: 'text' }), true);
    verifier('<textarea> → REFUSÉE', ouvreUnClavier({ tagName: 'TEXTAREA' }), true);
    verifier('<input type=email> (la newsletter) → REFUSÉE', ouvreUnClavier({ tagName: 'INPUT', type: 'email' }), true);
    verifier('contenteditable → REFUSÉE', ouvreUnClavier({ tagName: 'DIV', isContentEditable: true }), true);
    verifier('<input type=checkbox> → doit PASSER', ouvreUnClavier({ tagName: 'INPUT', type: 'checkbox' }), false);
    verifier('<body> — rien de focalisé → doit PASSER', ouvreUnClavier({ tagName: 'BODY' }), false);
    verifier('null → doit PASSER', ouvreUnClavier(null), false);
    /* Les deux fichiers portent la MÊME fonction, chacun servi de son côté.
       Ces deux cas font rougir la divergence le jour où elle arrive. */
    verifier('les deux copies s’accordent sur <select>, doit PASSER',
      ouvreUnClavier({ tagName: 'SELECT' }) === noyau.ouvreUnClavier({ tagName: 'SELECT' }), true);
    verifier('les deux copies s’accordent sur <input type=text>, doit PASSER',
      ouvreUnClavier({ tagName: 'INPUT', type: 'text' })
        === noyau.ouvreUnClavier({ tagName: 'INPUT', type: 'text' }), true);

    console.log('\n── LA FRONTIÈRE « ecart == ecartMax » — elle DEMANDE, ou elle constate');
    /* ⛔ TROUVÉ PAR MUTATION le 05/09 : `ecart > ecartMax` passé à `>=` laissait
       les DEUX bancs verts (25 + 114). Côté carte, le mode ne bouge pas — le
       rattrapage la garde flottante — mais elle cesse de DEMANDER pour se mettre
       à CONSTATER, à +2 du palier, le cas le plus courant. C'est la raison qui
       le dit, donc c'est elle qu'on fige. */
    verifier('7 maillots (marche à +2) → elle demande, doit PASSER', forme(noyau, T, 7, 2), 'marche');
    verifier('… et la raison dit « atteignable », doit PASSER',
      presence(noyau, { count: 7, tiers: T, page: 'index' }).raison.indexOf('atteignable') !== -1, true);
    verifier('… et elle ne parle PAS de « rien de demandable »',
      presence(noyau, { count: 7, tiers: T, page: 'index' }).raison.indexOf('rien de demandable'), -1);
    verifier('6 maillots (marche à +3) → la demande est REFUSÉE', forme(noyau, T, 6, 2), 'acquis');
    verifier('… et la raison le dit : elle constate',
      presence(noyau, { count: 6, tiers: T, page: 'index' }).raison.indexOf('rien de demandable') !== -1, true);
    verifier('… le noyau seul disait « aucun » à +3 — figé', noyau.decider({ count: 6, tiers: T }).mode, 'aucun');
    verifier('… et « flottant » à +2 — figé, doit PASSER', noyau.decider({ count: 7, tiers: T }).mode, 'flottant');

    console.log('\n' + (ko ? '⛔ ' + ko + ' cas en échec sur ' + (ok + ko) : '✅ ' + ok + ' cas figés passent'));
    return ko;
  }

  /* ── Sortie NODE : le banc, puis on s'arrête. ───────────────────────────
     ⚠️ NI `require` NI `__dirname` NI `import.meta` ICI, et les trois ont été
       essayés dans cet ordre :
         · `require` — la racine du dépôt a `"type": "module"` dans son
           package.json, donc tout `.js` est un module ES : « require is not
           defined in ES module scope ». C'est le banc qui l'a dit au premier
           lancement, pas ma relecture.
         · `import.meta.url` — valide en module ES, mais ce MÊME fichier est
           chargé par le navigateur en script classique, où `import.meta` est
           une ERREUR DE SYNTAXE. Le fichier entier serait mort en production
           pour une commodité de test. Un banc n'a pas le droit de coûter la
           page.
         · reste `process.argv[1]`, qui est le chemin de ce fichier tel que
           node l'a reçu, et `import()` dynamique — la seule forme d'import
           autorisée dans un script classique.
     ⚠️ LE NOYAU EST LU DEPUIS LE FICHIER VOISIN, PAS RECOPIÉ. Deux formules
       pour le même euro finissent par diverger d'un centime : si
       onix-barre-c.js bouge, ce banc tombe. C'est le comportement voulu. */
  if (typeof window === 'undefined') {
    if (typeof process !== 'undefined' && process.argv && process.argv.indexOf('--test') !== -1) {
      var moi = process.argv[1] || '';
      var chemin = moi.replace(/[/\\][^/\\]*$/, '') + '/onix-barre-c.js';
      import('node:fs').then(function (fs) {
        var src = fs.readFileSync(chemin, 'utf8');
        var faux = {};
        /* Un `document` factice, pas un `undefined` : le noyau appelle
           `document.readyState` en dernière ligne. `getElementById` rendant
           `null`, sa partie navigateur s'arrête d'elle-même — charger le
           noyau n'affiche pas le prototype C, ici comme dans la page. */
        var fauxDoc = { readyState: 'complete', getElementById: function () { return null; } };
        new Function('window', 'document', src)(faux, fauxDoc);
        if (!faux.OnixBarreC) {
          console.error('⛔ noyau window.OnixBarreC introuvable dans ' + chemin);
          process.exit(1);
        }
        process.exit(banc(faux.OnixBarreC) ? 1 : 0);
      }, function (e) {
        console.error('⛔ lecture impossible : ' + (e && e.message));
        process.exit(1);
      });
    }
    return;
  }

  racine.OnixCarteB = noyauLocal;
  noyauLocal.banc = banc;

  /* ═══════════════════════════════════════════════════════════════════════
     PARTIE NAVIGATEUR
     ═══════════════════════════════════════════════════════════════════════ */
  function demarrer() {
    var carte = document.getElementById('onix-carte-b');
    if (!carte) return;

    var noyau = racine.OnixBarreC;
    /* Le drapeau d'aperçu, lu UNE fois : `?voir-carte=N` dans l'adresse. */
    var APERCU = new URLSearchParams(location.search).get('voir-carte') !== null;
    if (!noyau || !noyau.decider) {
      try {
        console.error('[onix-design-b-carte] Le noyau window.OnixBarreC est absent : '
          + 'la carte reste masquée. La section doit charger onix-barre-c.js AVANT '
          + 'ce fichier, les deux en defer. Aucune formule n’est recopiée ici — '
          + 'une carte qui devine un montant est pire qu’une carte absente.');
      } catch (e) {}
      return;
    }

    if (document.getElementById('onix-discount-bar')) {
      try {
        console.warn('[onix-design-b-carte] La barre actuelle (#onix-discount-bar) est encore '
          + 'dans la page. Les deux écrivent --onix-bar-height, que back-to-top et le bouton '
          + 'collant de la fiche produit lisent pour se poser au-dessus.');
      } catch (e) {}
    }

    var tiers;
    try { tiers = JSON.parse(carte.dataset.tiers || '[]'); } catch (e) { return; }
    if (!tiers.length) return;
    tiers.sort(function (a, b) { return a.qty - b.qty; });

    var ecartMax = parseInt(carte.dataset.ecartMax, 10);
    if (!isFinite(ecartMax) || ecartMax < 1) ecartMax = noyau.ECART_MAX_DEFAUT;

    var prixRef = parseInt(carte.dataset.prixReference, 10);
    if (!isFinite(prixRef) || prixRef <= 0) prixRef = 2499;

    var page = carte.dataset.page || '';
    /* L'URL de la collection est RENDUE PAR LIQUID (`collections[…].url`), pas
       fabriquée ici : un handle renommé dans l'admin suit tout seul. Le repli
       est le chemin en dur, même idiome que header.liquid l.2510. */
    var URL_COLLECTION = carte.dataset.urlCollection || '';
    var introEl = document.getElementById('onix-carte-b-intro');
    var montantEl = document.getElementById('onix-carte-b-montant');
    var suffixeEl = document.getElementById('onix-carte-b-suffixe');
    var compteEl = document.getElementById('onix-carte-b-compte');
    var arcEl = document.getElementById('onix-carte-b-arc');
    var actionEl = document.getElementById('onix-carte-b-action');
    /* ⛔ UN SEUL ÉLÉMENT CLIQUABLE — 05/09, l'agent cliente : « elle a une croix,
       donc DEUX éléments cliquables ; ça contredit l'acquis "un seul élément
       cliquable" que seules la pilule et le compteur tenaient. »
       ➜ LA CROIX EST MASQUÉE PAR LE BALISAGE, ET PAR LUI SEUL : le
         `<button id="onix-carte-b-fermer">` porte l'attribut `hidden` dans
         sections/onix-design-b-carte.liquid. Rien, ici, ne le lui retire.

       ⛔ ET C'EST POURQUOI TOUT LE CÂBLAGE DE FERMETURE A ÉTÉ RETIRÉ LE 05/09 :
         il était INATTEIGNABLE, et il mentait trois fois.
           · Les trois `fermerEl.hidden = true` du rendu répétaient ce que le
             balisage disait déjà, et rendaient `marquerFermee()` inatteignable.
           · Le commentaire qu'ils portaient affirmait que la croix « sert au
             clavier et aux lecteurs d'écran ». C'est FAUX : `hidden` la sort du
             parcours de tabulation ET de l'arbre d'accessibilité. Elle ne
             servait ni l'un ni l'autre. Décider qu'elle doit revenir pour eux
             est SA décision, pas la mienne — et ce serait rouvrir « un seul
             élément cliquable », tranché le jour même.
           · Le nettoyage de l'aperçu visait `localStorage['onix-carte-b-fermee']`
             quand la seule clé écrite était `sessionStorage['onix_carte_b_ferme_v1']` :
             autre clé, autre stockage. Il ne nettoyait rien.
         Et la lecture, elle, n'était pas inoffensive : une valeur laissée en
         sessionStorage par une version où la croix s'affichait aurait fait taire
         la carte pour tout le reste de l'onglet, sans qu'aucun geste ne puisse
         la ramener.

       ⚠️ LE CONTRAT `ferme` RESTE DANS `presence()`, avec ses cas figés. C'est la
         partie pure : elle ne coûte rien, et c'est elle qu'on rebranche le jour
         où il redemande la croix. C'est le câblage navigateur qui est parti, pas
         la décision. */
    var place = document.getElementById('onix-carte-b-place');
    if (!introEl || !montantEl || !actionEl) return;

    var T = {
      restant: carte.dataset.tRestant || 'Ajoute {{n}} {{item}} pour {{price}}',
      debloquer: carte.dataset.tDebloquer || 'Ajoute {{n}} pour débloquer {{reward}}',
      economie: carte.dataset.tEconomie || 'Tu économises déjà {{amount}}',
      item: carte.dataset.tItem || 'maillot',
      items: carte.dataset.tItems || 'maillots',
      unite: carte.dataset.tUnite || 'maillots',
      ajouter: carte.dataset.tAjouter || 'Ajouter',
      panier: carte.dataset.tPanier || 'Voir le panier'
    };

    /* LA CIRCONFÉRENCE SE MESURE, ELLE NE SE RECOPIE PAS. Le rayon vit dans
       le SVG : le lire ici évite qu'un changement de gabarit laisse un
       `stroke-dasharray` figé sur l'ancienne valeur — un arc qui déborde ou
       s'arrête à mi-course, sans que rien ne lève. */
    var RAYON = arcEl ? (parseFloat(arcEl.getAttribute('r')) || 19) : 19;
    var TOUR = 2 * Math.PI * RAYON;
    if (arcEl) arcEl.style.strokeDasharray = TOUR.toFixed(2);

    /* Devise — miroir exact de fmtMoney() de cart-drawer-promo-suggest.js. */
    var tiroirRacine = document.querySelector('[data-cart-drawer-root]');
    var DEVISE = (tiroirRacine && tiroirRacine.dataset.cartCurrency)
      || (racine.Shopify && racine.Shopify.currency && racine.Shopify.currency.active) || 'EUR';
    /* ⛔ LE `|| 1` DÉGÉNÉRÉ, DÉJÀ PAYÉ CE MATIN SUR LE TIROIR DE PANIER.
       Un taux absent devant un client SUISSE retombait sur 1, donc ressuscitait
       le prix français en francs : « 7,49 » écrit en CHF là où ça vaut 7,18.
       Une promesse de 31 centimes qu'on ne tiendra pas. ➜ Hors EUR, un taux
       absent dégrade vers RIEN (NaN), jamais vers 1 : `montant()` le voit et
       l'appelant se tait. En EUR le taux vaut 1 par définition, pas par défaut. */
    var tauxBrut = parseFloat(racine.Shopify && racine.Shopify.currency && racine.Shopify.currency.rate);
    var TAUX = (DEVISE === 'EUR') ? 1 : (isFinite(tauxBrut) && tauxBrut > 0 ? tauxBrut : NaN);

    function montant(cents) {
      /* Un montant qu'on ne sait pas convertir ne s'écrit pas — il rend null,
         et c'est à l'appelant de se taire. Écrire « NaN » serait pire que rien. */
      if (!isFinite(cents)) return null;
      var a = (cents || 0) / 100;
      if (DEVISE === 'EUR') return a.toFixed(2).replace('.', ',') + '€';
      try { return new Intl.NumberFormat('en', { style: 'currency', currency: DEVISE, currencyDisplay: 'narrowSymbol' }).format(a); }
      catch (e) {
        try { return new Intl.NumberFormat('en', { style: 'currency', currency: DEVISE }).format(a); }
        catch (e2) { return a.toFixed(2) + ' ' + DEVISE; }
      }
    }

    /* ── ÉTAT LU, JAMAIS DEVINÉ ──────────────────────────────────────── */
    var LROOT = (racine.OnixCart && racine.OnixCart.cartRoot)
      || (racine.Shopify && racine.Shopify.routes && racine.Shopify.routes.root) || '/';
    if (LROOT.slice(-1) !== '/') LROOT += '/';

    var count = 0, base = 0, articles = [];
    var clavierOuvert = false;

    function tiroirOuvert() {
      return !!(tiroirRacine && tiroirRacine.classList.contains('is-open'));
    }
    function cookiesLeves() {
      return document.documentElement.classList.contains('onix-cookies-a-repondre');
    }

    /* ── LES ANCRES DU MODE « FLUX » ───────────────────────────────────
       Premier trouvé gagne. Aucune n'est obligatoire : sans ancre, le mode
       flux est indisponible sur cette page et la carte se tait. Elle ne se
       rabat JAMAIS en flottante par défaut — ce serait revenir au défaut
       qu'on corrige. */
    function trouverAncre() {
      if (page === 'cart') {
        var resume = document.querySelector('.onix-cart-page__summary');
        var bouton = document.querySelector('.onix-cart-page__checkout');
        if (resume && bouton) return { parent: resume, avant: bouton };
        return null;
      }
      var explicite = document.querySelector('[data-onix-carte-ancre], [data-onix-barre-ancre]');
      if (explicite) return { parent: explicite, avant: null };
      var barreOutils = document.querySelector('.onix-collection__toolbar');
      if (barreOutils && barreOutils.parentNode)
        return { parent: barreOutils.parentNode, avant: barreOutils.nextElementSibling };
      return null;
    }

    var ancrePosee = null;
    function placer(mode) {
      if (mode === 'flux') {
        var a = trouverAncre();
        if (!a) return false;
        if (carte.parentNode !== a.parent || (a.avant && carte.nextElementSibling !== a.avant)) {
          a.parent.insertBefore(carte, a.avant || null);
        }
        ancrePosee = a;
        if (place) place.style.height = '0px';
        return true;
      }
      /* Flottante : on garde la place que la carte occupait dans le flux,
         sinon la grille remonte sous les doigts au moment de l'ajout. */
      if (ancrePosee && place && place.parentNode !== ancrePosee.parent) {
        ancrePosee.parent.insertBefore(place, carte.nextSibling);
      }
      if (place && carte.parentNode === (ancrePosee && ancrePosee.parent)) {
        var h = Math.ceil(carte.getBoundingClientRect().height);
        if (h > 0) place.style.height = h + 'px';
      }
      if (carte.parentNode !== document.body) document.body.appendChild(carte);
      return true;
    }

    /* La hauteur publiée : back-to-top et le bouton collant de la fiche
       produit s'y posent au-dessus. En mode flux elle vaut 0 — la carte est
       dans le document, elle ne confisque rien. */
    function publierHauteur(mode) {
      var h = 0;
      if (mode === 'flottant' && !carte.hidden) {
        h = Math.ceil(carte.getBoundingClientRect().height) + 12;
      }
      document.documentElement.style.setProperty('--onix-bar-height', h + 'px');
    }

    function echapper(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    /* ── LE RENDU ──────────────────────────────────────────────────────── */
    var dernierMode = null;

    function peindre(bloc) {
      introEl.textContent = bloc.intro;
      montantEl.textContent = bloc.montant;
      if (suffixeEl) suffixeEl.textContent = bloc.suffixe;
    }

    /* ⛔ EN MODE APERÇU, LES AUTRES SURFACES NE LA FONT PAS TAIRE — 05/09, 16h00.
       En vrai, la carte cède le passage : tiroir ouvert, clavier, bandeau cookies,
       pop-up de bienvenue. C'est juste. Mais sur un lien d'aperçu, la pop-up de
       bienvenue s'ouvre à l'arrivée et la carte reste à hauteur 0 — le lien ne
       montre rien, et `montrer.js` a REFUSÉ de me le donner (il a bien fait).
       ➜ `?voir-carte=N` neutralise les silences CONTEXTUELS (tiroir, clavier,
         cookies, fermée par le client) et RIEN d'autre. Les silences de fond
         restent : fiche produit, dernier palier, marche hors de portée. Un aperçu
         qui montrerait une carte que la production n'affiche jamais serait pire
         qu'un aperçu vide. */
    function rendre() {
      var d = presence(noyau, {
        count: count,
        tiers: tiers,
        ecartMax: ecartMax,
        page: page,
        tiroirOuvert: APERCU ? false : tiroirOuvert(),
        clavier: APERCU ? false : clavierOuvert,
        cookies: APERCU ? false : cookiesLeves(),
        /* La croix est masquée par le balisage : rien ne peut fermer la carte
           aujourd'hui. Voir « UN SEUL ÉLÉMENT CLIQUABLE » plus haut. */
        ferme: false,
        ancre: APERCU ? true : !!trouverAncre()
      });

      if (d.mode === 'aucun') {
        carte.dataset.raison = d.raison;
        carte.hidden = true;
        carte.classList.remove('onix-carte--flottante', 'onix-carte--flux');
        if (place) place.style.height = '0px';
        dernierMode = 'aucun';
        publierHauteur('aucun');
        return;
      }

      /* ⛔ LA RAISON S'ÉCRIT APRÈS LE VERDICT, JAMAIS AVANT — et le verdict
         n'est complet qu'une fois `placer()` passé. Elle était écrite en
         amont : sur la page panier vide, l'inspecteur annonçait « page
         panier — dans le flux » sur une carte masquée quelques lignes plus
         bas. Et l'échec de placement ne masque plus : il replie en flottant.
         `placer('flottant')` ne peut pas échouer — il appendChild sur body. */
      if (!placer(d.mode)) {
        d = { mode: 'flottant', raison: d.raison + ' — ancre disparue au moment du rendu, repli flottant' };
        placer('flottant');
      }
      carte.dataset.raison = d.raison;   /* lisible dans l'inspecteur, et testable */

      carte.classList.toggle('onix-carte--flottante', d.mode === 'flottant');
      carte.classList.toggle('onix-carte--flux', d.mode === 'flux');

      var suiv = noyau.marcheSuivante(tiers, count);
      var atteint = noyau.palierAtteint(tiers, count);
      var f = forme(noyau, tiers, count, ecartMax);

      carte.classList.toggle('onix-carte--cible', f === 'cible');
      carte.classList.toggle('onix-carte--acquis', f === 'acquis');

      if (f === 'cible') {
        /* LA PROMESSE, EN EUROS. Le montant se calcule sur le prix de
           référence du catalogue, donc le plus bas — tout panier réel
           économise DAVANTAGE. Voir l'en-tête de la section.
           ⚠️ LA CONDITION EST `f === 'cible'` ET PLUS `count <= 0`. C'est
             elle qui empêche « Tu économises déjà 0,00 € » : quand rien
             n'est acquis et que la marche est hors de portée, `forme()`
             rend `cible` avec un panier NON vide. On annonce alors ce qui
             manque pour la marche visée, pas la totalité du palier. */
        var cible = suiv || tiers[0];
        var manqueCible = count > 0 ? Math.max(1, cible.qty - count) : cible.qty;
        var gainRef = noyau.gainCentimes(cible, prixRef * cible.qty, TAUX);
        peindre(composer(
          T.debloquer
            .replace('{{n}}', String(manqueCible) + ' ' + T.unite)
            .replace('{{reward}}', '{{price}}'),
          montant(gainRef)
        ));
        /* ⛔ L'ANNEAU DIT TOUJOURS CE QU'IL A, JAMAIS CE QU'IL LUI MANQUE.
           Défaut trouvé par l'agent cliente le 05/09 : « panier vide il affiche
           "2" et ça veut dire ajoutes-en 2 ; à 1 maillot il affiche "1" et ça veut
           dire tu en as 1. Je ne saurais pas le formuler mais ça me trouble une
           demi-seconde. » Le même chiffre voulait dire deux choses selon l'écran.
           ➜ Un seul sens : le compte du panier. À vide, c'est 0. */
        if (compteEl) compteEl.textContent = String(count);
        actionEl.hidden = true;              /* rien n'est demandé : rien à taper */
        /* ⚠️ LA CROIX EXISTE DÈS QUE LA CARTE FLOTTE, y compris à panier
           vide. Avant, elle était masquée dans cet état — c'était sans
           conséquence tant que la carte n'apparaissait qu'en flux, qui
           défile. Elle flotte maintenant sur toutes les pages : une surface
           posée en bas d'écran sans moyen de la retirer, c'est le défaut
           qu'on répare, pas un détail. */
      } else if (f === 'marche') {
        var manque = suiv.qty - count;
        /* Le MÊME chemin que le banc — voir `prixMarcheSuivante()`. Appeler
           le noyau en direct ici remettrait le calcul hors de portée des cas
           figés, et c'est exactement le défaut du 05/09. */
        var marginal = prixMarcheSuivante(noyau, tiers, count, base, TAUX, articles);
        peindre(composer(
          T.restant
            .replace('{{n}}', String(manque))
            .replace('{{item}}', manque > 1 ? T.items : T.item),
          montant(marginal)
        ));
        if (compteEl) compteEl.textContent = String(count);
        actionEl.hidden = false;
        actionEl.textContent = T.ajouter;
      } else {
        /* PALIER ACQUIS : on ne demande rien, on constate. La coche remplace
           le chiffre — « c'est fait » n'est pas un nombre.
           ⚠️ Un montant NU (« 69,72€ ») ne se lit pas : le client ne sait pas
             si c'est ce qu'il paie ou ce qu'il gagne. `cart.progress_savings`
             existe et est traduite dans les six langues.
           ⛔ C'EST L'ÉTAT DU TROU 5-6, ET IL EST DÉSORMAIS ATTEIGNABLE. La
             marche suivante est à +4 puis +3 : un mur, qu'on ne demande pas.
             Ce qui reste à l'écran est ce qui est DÉJÀ GAGNÉ, en euros, avec
             l'anneau plein et la coche — et le bouton mène au panier, pas
             vers un palier hors de portée. Aucune poussée dans le vide.
           ⚠️ `atteint` ne peut plus être `null` ici : `forme()` rend `cible`
             quand aucun palier n'est acquis. Le garde reste, il ne coûte
             rien et il documente pourquoi le 0,00 € n'arrive plus. */
        var gain = atteint ? noyau.gainCentimes(atteint, base, TAUX) : 0;
        peindre(composer(T.economie, montant(gain), '{{amount}}'));
        /* Le compteur suit le panier même sous la coche : elle le masque en
           CSS, elle ne le remplace pas dans le DOM — et il redevient visible
           à la marche suivante. */
        if (compteEl) compteEl.textContent = String(count);
        actionEl.hidden = false;
        actionEl.textContent = T.panier;
      }

      if (arcEl) {
        arcEl.style.strokeDashoffset = (TOUR * (1 - fraction(noyau, tiers, count, ecartMax))).toFixed(2);
      }

      carte.hidden = false;
      dernierMode = d.mode;
      requestAnimationFrame(function () { publierHauteur(d.mode); });
    }

    /* ── L'ACTION : UN SEUL GESTE, VERS CE QU'IL FAUT FAIRE MAINTENANT ───
       ⛔ « AJOUTER » NE MÈNE PLUS AU PANIER — 05/09. Le bouton demandait un
         maillot de plus et ouvrait le tiroir, c'est-à-dire l'endroit où il n'y
         a aucun maillot à prendre : le client devait ressortir pour obéir. Il
         mène désormais à `les-meilleurs-maillots-1` (voir `destinationAction()`
         pour le choix, sa raison et son URL vérifiée).
       ⛔ « VOIR LE PANIER » N'A PAS BOUGÉ D'UN POUCE : palier acquis, le tiroir
         garde l'offre « 2ᵉ maillot », les suggestions, l'échelle des paliers et
         l'application automatique du code. La carte n'en refait aucun, elle
         ouvre le tiroir — et si aucun ouvreur n'existe dans la page, elle
         retombe sur la page panier, exactement comme avant.
       ⚠️ La forme est RELUE au moment du clic, jamais mémorisée au rendu : le
         panier peut avoir changé entre les deux (ajout depuis le tiroir), et
         un bouton qui garde la destination d'un état périmé est un bouton qui
         ment. C'est le même appel que `rendre()`, pas une copie. */
    actionEl.addEventListener('click', function (e) {
      e.preventDefault();
      var d = destinationAction(forme(noyau, tiers, count, ecartMax), URL_COLLECTION, LROOT);
      if (d.panier) {
        var ouvreur = document.querySelector('[data-cart-drawer-open], .site-header__cart, [href$="/cart"]');
        if (ouvreur && ouvreur.click) { ouvreur.click(); return; }
      }
      racine.location.href = d.url;
    });

    /* ── LECTURE DU PANIER ───────────────────────────────────────────────
       Le décompte EXCLUT les lignes de l'offre découverte (`_offer_bundle`) :
       c'est une offre à part, aux contraintes différentes (12 maillots, sans
       flocage) ; la compter afficherait un palier que le client ne peut pas
       atteindre par ce chemin. Le repli optimiste de cart-drawer.js est repris
       tel quel — sans lui, l'anneau se remplit puis retombe à 0 en 300 ms le
       temps que Shopify rende les propriétés. */
    var enCours = false, enAttente = false, minuteur = null;

    function lire() {
      return fetch(LROOT + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (cart && cart.currency) DEVISE = cart.currency;
          var items = (cart && cart.items) || [];
          var qteBundle = 0, baseBundle = 0;
          items.forEach(function (i) {
            if (i.properties && i.properties._offer_bundle === 'true') {
              qteBundle += (i.quantity || 0);
              baseBundle += (i.original_line_price || 0);
            }
          });
          var optQte = Number(racine.__onixOptimisticBundleQty || 0);
          var optJusqua = Number(racine.__onixOptimisticBundleUntil || 0);
          if (optQte > 0 && Date.now() < optJusqua && qteBundle < optQte) {
            setTimeout(lire, 250);   /* on attend plutôt que peindre un état à démentir */
            return;
          }
          count = Math.max(0, (cart.item_count || 0) - qteBundle);
          base = Math.max(0, (cart.original_total_price || 0) - baseBundle);
          /* ⛔ ON GARDE LA LISTE, PAS SON MINIMUM. Réduire ici mettait le
             `uniteMinPanier()` hors du chemin testable : le banc ne pouvait
             plus le traverser, et trois mutations restaient vertes (05/09). */
          articles = items;
          rendre();
        })
        .catch(function () { /* on garde le dernier état connu */ });
    }

    function rafraichir() {
      if (enCours) { enAttente = true; return; }
      if (minuteur) return;
      minuteur = setTimeout(function () {
        minuteur = null;
        enCours = true;
        lire().then(function () {
          enCours = false;
          if (enAttente) { enAttente = false; rafraichir(); }
        });
      }, 50);
    }

    document.addEventListener('cart:refresh', rafraichir);
    racine.addEventListener('pageshow', function (e) { if (e.persisted) rafraichir(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') rafraichir();
    });
    racine.addEventListener('resize', function () { publierHauteur(dernierMode); });

    /* Le clavier mobile réduit le viewport : une carte `fixed` viendrait se
       coller au milieu de l'écran, au-dessus du champ en cours de saisie.
       Le test est `ouvreUnClavier()`, dans la partie pure : figé au banc dans
       les deux sens, et un `<select>` n'y déclenche plus rien. */
    document.addEventListener('focusin', function (e) {
      if (ouvreUnClavier(e.target)) { clavierOuvert = true; rendre(); }
    });
    document.addEventListener('focusout', function () {
      setTimeout(function () {
        if (!ouvreUnClavier(document.activeElement)) { clavierOuvert = false; rendre(); }
      }, 150);
    });

    /* ⛔ ELLE NE REVENAIT PAS APRÈS LE CONSENTEMENT — mesuré le 05/09 à 18h50,
       et c'est ÇA, « sur PC la carte n'apparaît pas ».
       Le bandeau cookies libère le bas en RETIRANT `onix-cookies-a-repondre`
       de <html> ; il n'émet aucun événement, et c'est délibéré de son côté
       (« un état se libère là où il cesse d'être vrai »). La carte, elle, ne
       se repeignait que sur `cart:refresh`, `pageshow`, `visibilitychange` et
       le clavier. Résultat mesuré, thème d'aperçu, 1280×800, profil vierge :
       après un clic sur « Accepter », `hidden` valait ENCORE true et
       `data-raison` disait ENCORE « bandeau cookies levé — il occupe déjà le
       bas ». Pour le reste de la page. Sur un téléphone déjà visité le
       bandeau n'apparaît jamais et la carte s'affiche ; sur l'ordinateur où
       l'on répond une fois et où l'on ne recharge pas, elle ne revenait pas
       de la visite. Le défaut n'était pas une largeur, c'était un réveil
       manquant.
       ➜ On observe l'attribut `class` de <html>, et RIEN d'autre.
       ⚠️ `attributeFilter: ['class']` N'EST PAS UNE OPTIMISATION. Sans lui,
         l'observateur voit aussi `style`, où `publierHauteur()` écrit
         `--onix-bar-height` à chaque rendu : le rendu déclencherait
         l'observateur qui déclencherait le rendu. C'est exactement la boucle
         que `cookie-banner.liquid` documente pour refuser un observateur.
       ⚠️ ET LA GARDE SUR LA VALEUR NON PLUS : d'autres composants posent leurs
         classes sur <html> (`onix-langue-ouverte`, `onix-carte-b-apercu`). On
         ne repeint que si la réponse aux cookies a VRAIMENT changé. */
    var cookiesDernier = cookiesLeves();
    try {
      new MutationObserver(function () {
        var maintenant = cookiesLeves();
        if (maintenant === cookiesDernier) return;
        cookiesDernier = maintenant;
        rendre();
      }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    } catch (e) { /* pas de MutationObserver : on garde l'ancien comportement */ }

    /* Exposé pour l'inspection et pour un banc navigateur. */
    racine.OnixCarteB.etat = function () {
      return {
        count: count, base: base, unite: uniteMinPanier(articles), mode: dernierMode,
        raison: carte.dataset.raison,
        forme: forme(noyau, tiers, count, ecartMax),
        anneau: Math.round(fraction(noyau, tiers, count, ecartMax) * 100),
        hauteur: Math.round(carte.getBoundingClientRect().height)
      };
    };
    /* Le 3ᵉ argument est le prix à l'unité le plus bas. Omis, on retombe sur
       la moyenne — c'est-à-dire sur le défaut du panier mixte : un banc
       navigateur qui l'oublie mesurerait l'ancien comportement. */
    racine.OnixCarteB.simuler = function (c, b, u) {
      count = c; base = b;
      /* ⚠️ ON FABRIQUE UN ARTICLE plutôt que de poser le minimum en dur : le
         banc navigateur doit traverser `uniteMinPanier()` comme le vrai
         panier, sinon il mesure un chemin que la production n'emprunte pas. */
      articles = (u == null) ? [] : [{ original_price: u, quantity: 1 }];
      rendre();
    };

    /* ⛔ LA VOIR AVANT DE LA BRANCHER — sa demande du 05/09, 15h58 :
       « je pourrais la voir avant de la brancher, ce serait le minimum non ? ».
       La carte ne s'affiche qu'avec un panier : sur un lien d'aperçu, panier
       vide, elle reste à hauteur 0 et le lien ne montre rien. Le garde
       `montrer.js` a d'ailleurs REFUSÉ de me le donner.
       ➜ `?voir-carte=3` joue un panier de 3 maillots au prix d'entrée. C'est un
         paramètre d'URL : aucun visiteur ne l'a jamais.
       ⚠️ Il passe par `simuler()`, le MÊME crochet que le banc navigateur, donc
         le même chemin de calcul que la production. Un aperçu qui court-circuite
         le calcul montre une carte qui n'existe pas. */
    var vc = new URLSearchParams(location.search).get('voir-carte');
    if (vc !== null) {
      /* Débloque la règle CSS qui masque toute carte flottante tant que le
         bandeau cookies n'a pas de réponse — voir le commentaire dans le
         fichier .liquid, à côté de cette même règle. */
      document.documentElement.classList.add('onix-carte-b-apercu');
      /* ⛔ `|| 3` MANGEAIT LE ZÉRO — trouvé le 05/09 en mesurant les six états à
         1280 px : `?voir-carte=0` rendait un panier de 3. L'état PANIER VIDE
         était donc le seul impossible à montrer, alors que c'est le premier
         que voit un visiteur. `0 || 3` vaut 3 en JS ; c'est `NaN` qu'il faut
         rattraper, pas la valeur fausse. */
      var n = parseInt(vc, 10);
      if (!isFinite(n) || n < 0) n = 3;
      var pu = parseInt(carte.getAttribute('data-prix-reference'), 10) || 2499;
      racine.OnixCarteB.simuler(n, n * pu, pu);
      return;
    }

    rafraichir();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer);
  else demarrer();
})(typeof window !== 'undefined' ? window : globalThis);
